import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { m, AnimatePresence } from "framer-motion";
import MultiStepForm, { FieldProps, variantsChildren } from "./form";
import SuccessMessage from "./successMessage";
import { launchConfetti } from "@/app/lib/confetti";
import { normalizePhoneToE164 } from "@/app/lib/phone";
import { formatPragueTimestamp } from "@/app/lib/formatTime";
import { buildAttributionSnapshot } from "@/app/lib/attributionSnapshot";

export interface FormWizardStepProps {
  id: string;
  fields: Array<FieldProps>;
}

const steps: FormWizardStepProps[] = [
  {
    id: "calculator",
    fields: [
      {
        name: "businessType",
        type: "radio",
        options: ["eshop", "servicesLeads", "localBusiness", "projectStartup"],
      },
      {
        name: "mainGoal",
        type: "radio",
        options: ["moreOrders", "stablePerformance", "growth", "notWorking"],
      },
      {
        name: "measurement",
        type: "radio",
        options: ["noIdea", "basicWorks", "dontTrust", "advanced"],
      },
    ],
  },
  {
    id: "contact",
    fields: [
      { name: "name", type: "text", required: true },
      { name: "company", type: "text", required: false },
      { name: "email", type: "email", required: true },
      { name: "phone", type: "tel", required: false },
      { name: "message", type: "textarea", required: false },
      {
        name: "monthlyBudget",
        type: "slider",
        min: 50000,
        max: 500000,
        step: 1000,
        isCurrency: true,
      },
      { name: "quote", type: "hidden" },
      { name: "acceptTermsQuote", type: "checkbox", required: true },
    ],
  },
];

const FormCta = () => {
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const t = useTranslations("form");

  const defaultValues = {
    monthlyBudget: 50000,
  };

  const handleSubmit = async (values: Record<string, any>) => {
    const apiEndpoint = "/api/cta-email";
    setSuccess(false);
    setError(null);
    setSubmitting(true);

    const normalizedPhone = normalizePhoneToE164(values.phone);
    const nameTrimmed = (values.name || "").trim();
    const nameParts = nameTrimmed ? nameTrimmed.split(/\s+/) : [];
    const firstName = nameParts[0] || null;
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : null;
    const company = (values.company || "").trim();

    const payload = {
      ...values,
      phone: normalizedPhone ?? values.phone ?? null,
      company: company || null,
      first_name: firstName,
      last_name: lastName,
      form_name: "cta",
      form_submit_time: formatPragueTimestamp(),
      attribution_snapshot: buildAttributionSnapshot(),
    };

    try {
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (!responseData.success) {
        const message =
          responseData.errors || responseData.error || "Něco se pokazilo";
        setError(
          typeof message === "string" ? message : JSON.stringify(message),
        );
        setSubmitting(false);
        return Promise.reject(message);
      } else {
        setSuccess(true);
        setSubmitting(false);
        launchConfetti();
        window.dataLayer?.push({
          event: "generate_lead",
          form_name: "cta",
          business_type: values.businessType,
          main_goal: values.mainGoal,
          measurement: values.measurement,
          monthly_budget: values.monthlyBudget,
        });
        return Promise.resolve();
      }
    } catch (err: any) {
      setError("Network or server error: " + err.message);
      setSubmitting(false);
      window.dataLayer?.push({
        event: "form_error",
        form_name: "cta",
        error_message: err.message,
      });
      return Promise.reject(err);
    }
  };

  const [values, setValues] = useState<Record<string, any>>({});
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const activeStep = useMemo(() => steps[activeStepIndex], [activeStepIndex]);
  const isLastStep = useMemo(
    () => activeStepIndex === steps.length - 1,
    [activeStepIndex],
  );

  const goNextStep = () => {
    setActiveStepIndex((index) => (index += 1));
  };

  const goPrevStep = () => {
    setActiveStepIndex((index) => (index -= 1));
  };

  const handleNextStep = async (stepValues: Record<string, any>) => {
    const newValues = { ...values, ...stepValues };
    setValues(newValues);

    if (isLastStep) {
      await handleSubmit(newValues);
    } else {
      window.dataLayer?.push({
        event: "form_step_complete",
        form_name: "cta",
        form_step: activeStepIndex + 1,
        business_type: newValues.businessType,
        main_goal: newValues.mainGoal,
        measurement: newValues.measurement,
      });
      goNextStep();
    }
  };

  const handleBackStep = (stepValues: Record<string, any>) => {
    const newValues = { ...values, ...stepValues };
    setValues(newValues);
    goPrevStep();
  };

  if (!activeStep) {
    return null;
  }

  return (
    <AnimatePresence mode="wait">
      <m.div
        key={success ? "success" : activeStep.id}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ staggerChildren: 0.1 }}
        className="min-h-full flex flex-col justify-center items-center w-full"
        id="quote-form"
      >
        {success ? (
          <SuccessMessage closeButton={true} />
        ) : (
          <m.div
            key={activeStep.id}
            id={activeStep.id}
            variants={variantsChildren}
            className="w-full max-w-md flex flex-col items-center gap-8"
          >
            <h3 className="h4 uppercase text-center">
              {t(`${activeStep.id}Title`)}
            </h3>
            <MultiStepForm
              activeStepId={activeStep.id}
              key={activeStep.id}
              fields={activeStep.fields}
              defaultValues={{ ...defaultValues, ...values }}
              showBackButton={!!activeStepIndex}
              onSubmit={handleNextStep}
              onBack={handleBackStep}
              isLastStep={activeStepIndex === steps.length - 1}
              submitting={submitting}
              progressive={activeStepIndex === 0}
            />
            {error && (
              <p className="text-red-500 text-sm text-center">
                {String(error)}
              </p>
            )}
          </m.div>
        )}
      </m.div>
    </AnimatePresence>
  );
};
export default FormCta;
